/*
 * <product-video-reels> — vertical video viewer for the "Video reels" product
 * block (snippets/product-video-reels.liquid). Extends ModalDialog (global.js):
 * Escape, focus trap and body scroll-lock come from there. One reel per view,
 * swipe / thumbnail strip / arrow keys to switch; autoplay muted with mute and
 * pause controls; auto-advances when a video ends; product card with a one-tap
 * add to cart that hands the response to the cart drawer / notification.
 */
(function () {
  if (typeof ModalDialog === 'undefined' || customElements.get('product-video-reels')) return;

  class ProductVideoReels extends ModalDialog {
    constructor() {
      super();
      this.track = this.querySelector('[data-reels-track]');
      this.slides = Array.from(this.querySelectorAll('[data-reel-slide]'));
      this.videos = this.slides.map((slide) => slide.querySelector('video'));
      this.thumbs = Array.from(this.querySelectorAll('[data-reel-thumb]'));
      this.index = 0;
      this.muted = true;
      this.paused = false;
      this.scrollTimer = null;

      if (!this.track || !this.slides.length) return;

      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        }, 80);
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.reelThumb), true));
      });

      this.slides.forEach((slide, i) => {
        const video = this.videos[i];
        const mute = slide.querySelector('[data-reel-mute]');
        const pause = slide.querySelector('[data-reel-pause]');
        const add = slide.querySelector('[data-reel-add]');

        if (mute) mute.addEventListener('click', () => this.toggleMute());
        if (pause) pause.addEventListener('click', () => this.togglePause());
        if (add) add.addEventListener('click', () => this.addToCart(add, slide));
        if (video) {
          video.addEventListener('click', () => this.togglePause());
          video.addEventListener('ended', () => {
            if (this.index === i && this.hasAttribute('open')) this.goTo((i + 1) % this.slides.length, true);
          });
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(Math.min(this.index + 1, this.slides.length - 1), true);
        if (event.key === 'ArrowLeft') this.goTo(Math.max(this.index - 1, 0), true);
      });

      // click on the backdrop closes (ModalDialog only handles <modal-dialog> itself)
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    show(opener) {
      if (this.closing) return;
      this.mountToBody(); // before super.show() so the focus trap targets the moved node
      super.show(opener); // sets [open], locks body, traps focus, pauses page media
      const start = opener && opener.dataset.reelOpen ? Number(opener.dataset.reelOpen) : 0;
      this.paused = false;
      window.requestAnimationFrame(() => {
        this.goTo(start, false);
        this.play();
      });
    }

    hide() {
      if (this.closing) return;
      this.pauseAll();
      if (!this.hasAttribute('open') || this.reducedMotion()) {
        super.hide();
        this.unmountFromBody();
        return;
      }
      // play the exit animation (CSS .is-closing), then let ModalDialog remove [open]
      this.closing = true;
      this.classList.add('is-closing');
      const done = () => {
        clearTimeout(timer);
        this.removeEventListener('animationend', done);
        this.classList.remove('is-closing');
        this.closing = false;
        super.hide();
        this.unmountFromBody();
      };
      const timer = setTimeout(done, 300);
      this.addEventListener('animationend', done);
    }

    /*
     * While open, the viewer is a direct child of <body>. Rendered in place it sits
     * inside the product info / media column, and any ancestor that creates a
     * stacking context traps its position: fixed + z-index. "Enable sticky product
     * information" does exactly that (.product__info-container--sticky is
     * position: sticky + z-index: 2), so the site header painted over the backdrop
     * and hid the dialog's title bar - on some demos only, depending on that setting.
     * It is moved back to its block on close, so theme-editor re-renders still own it.
     */
    mountToBody() {
      if (!this.parentNode || this.parentNode === document.body) return;
      if (!this.homeMarker) this.homeMarker = document.createComment('product-video-reels');
      this.parentNode.insertBefore(this.homeMarker, this);
      document.body.appendChild(this);
    }

    unmountFromBody() {
      const marker = this.homeMarker;
      if (!marker || this.parentNode !== document.body) return;
      if (marker.isConnected) marker.parentNode.replaceChild(this, marker);
      else this.remove(); // its section was re-rendered in the theme editor while open
    }

    reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    goTo(index, smooth) {
      const i = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
      this.setActive(i);
    }

    setActive(i) {
      this.index = i;
      this.thumbs.forEach((thumb, t) => {
        if (t === i) {
          thumb.setAttribute('aria-current', 'true');
          const strip = thumb.closest('[data-reels-thumbs]');
          if (strip) strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'smooth' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      this.videos.forEach((video, v) => {
        if (!video || v === i) return;
        video.pause();
        video.currentTime = 0;
      });
      this.syncButtons();
      if (this.hasAttribute('open') && !this.paused) this.play();
    }

    play() {
      const video = this.videos[this.index];
      if (!video) return;
      video.muted = this.muted;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          // autoplay with sound was blocked: fall back to muted
          if (!this.muted) {
            this.muted = true;
            video.muted = true;
            this.syncButtons();
            video.play().catch(() => {});
          }
        });
      }
    }

    pauseAll() {
      this.videos.forEach((video) => video && video.pause());
    }

    toggleMute() {
      this.muted = !this.muted;
      this.videos.forEach((video) => video && (video.muted = this.muted));
      this.syncButtons();
      if (!this.paused) this.play();
    }

    togglePause() {
      this.paused = !this.paused;
      if (this.paused) this.videos[this.index] && this.videos[this.index].pause();
      else this.play();
      this.syncButtons();
    }

    syncButtons() {
      this.querySelectorAll('[data-reel-mute]').forEach((button) => {
        button.setAttribute('aria-pressed', this.muted ? 'true' : 'false');
        button.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
      });
      this.querySelectorAll('[data-reel-pause]').forEach((button) => {
        button.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
        button.setAttribute('aria-label', this.paused ? 'Play' : 'Pause');
      });
    }

    addToCart(button, slide) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.classList.contains('loading')) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const error = slide.querySelector('.product-video-reels__error');
      const showError = (message) => {
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
        clearTimeout(this.errorTimer);
        this.errorTimer = setTimeout(() => (error.hidden = true), 4000);
      };

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');

      fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartError, { source: 'product-video-reels', productVariantId: variantId, errors: response.errors || response.description, message: response.message });
            }
            showError(response.description || response.message || 'Could not add to cart');
            return;
          }
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-video-reels', productVariantId: variantId, cartData: response });
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          // close the viewer, then let the drawer / notification take over
          this.hide();
          cart.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
          showError('Could not add to cart');
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }
  }

  customElements.define('product-video-reels', ProductVideoReels);

  // Openers: the thumbnail row in the block (one viewer per block)
  const bind = (root) => {
    const inactive = !(window.Shopify && Shopify.designMode) && /(^|; )inspd_s=[02]/.test(document.cookie);
    root.querySelectorAll('.product-video-reels').forEach((block) => {
      if (inactive) { block.style.display = 'none'; return; }
      if (block.dataset.reelsBound) return;
      block.dataset.reelsBound = 'true';
      const viewer = block.querySelector('product-video-reels');
      if (!viewer) return;
      block.querySelectorAll('[data-reel-open]').forEach((button) => {
        button.addEventListener('click', () => viewer.show(button));
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(document));
  } else {
    bind(document);
  }
  document.addEventListener('shopify:section:load', (event) => bind(event.target || document));
  // theme editor: a section removed / re-rendered while its viewer is open on <body>
  document.addEventListener('shopify:section:unload', (event) => {
    document.querySelectorAll('body > product-video-reels').forEach((viewer) => {
      if (!viewer.homeMarker || !event.target || !event.target.contains(viewer.homeMarker)) return;
      viewer.pauseAll();
      document.body.classList.remove('overflow-hidden');
      viewer.remove();
    });
  });
})();
